/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Import UI component (S41a-DN)
import { JointVentureInvestmentDetailedRecordInfo } from "./info";
import { JointVentureInvestmentDetailedRecordService } from "services/joint-venture-investment-detailed-record";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

const createItemDefault = () => ({
  documentNumber: "",
  documentDate: "",
  description: "",
  openingValue: "",
  profitShareAdjustment: "",
  differentReportingDateAdjustment: "",
  policyAdjustment: "",
  equityAdjustment: "",
  closingValue: "",
});

const createGroupDefault = () => ({
  jointVentureName: "",
  items: [createItemDefault()],
});

const initialValues = {
  unit: "",
  address: "",
  fiscalYear: today.getFullYear().toString(),
  groups: [], // Sẽ được init trong useEffect nếu rỗng
  totalPages: "1",
  openingDate: "",
  reportDate: "",
  ledgerClerk: "",
  chiefAccountant: "",
  legalRepresentative: "",
  // Phụ trợ UI chữ ký
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
};

function JointVentureInvestmentDetailedRecordDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [getData, setGetData] = useState({});
  const [formInitials, setFormInitials] = useState(initialValues);

  useEffect(() => {
    (async () => {
      try {
        let apiData = {};
        if (id) {
          const response = await JointVentureInvestmentDetailedRecordService.getById(id);
          apiData = response.data ? response.data : response;
        }

        const formatDateVN = (isoString) => {
          if (!isoString) return "";
          if (typeof isoString === "string" && isoString.includes("/")) return isoString;
          const d = new Date(isoString);
          if (isNaN(d.getTime())) return isoString;
          return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(
            2,
            "0"
          )}/${d.getFullYear()}`;
        };

        let groups = apiData.groups || [];
        if (groups.length === 0) {
          groups = [createGroupDefault()];
        } else {
          // Format date cho từng item trong group
          groups = groups.map(g => ({
            ...g,
            items: (g.items || []).map(it => ({
                ...it,
                documentDate: formatDateVN(it.documentDate)
            }))
          }));
        }

        // Parse reportDate -> signingDay/Month/Year
        let sDay = initialValues.signingDay;
        let sMonth = initialValues.signingMonth;
        let sYear = initialValues.signingYear;
        const rDate = apiData.reportDate || "";
        if (rDate) {
          const parts = rDate.split("/");
          if (parts.length === 3) {
            sDay = parts[0];
            sMonth = parts[1];
            sYear = parts[2];
          }
        }

        const loadedData = {
          ...initialValues,
          unit: apiData.unit || apiData.company?.name || "",
          address: apiData.address || apiData.company?.address || "",
          fiscalYear: apiData.fiscalYear || apiData.year || initialValues.fiscalYear,
          groups,
          totalPages: apiData.totalPages || "1",
          openingDate: formatDateVN(apiData.openingDate),
          reportDate: rDate,
          signingDay: sDay,
          signingMonth: sMonth,
          signingYear: sYear,
          ledgerClerk: apiData.ledgerClerk || "",
          chiefAccountant: apiData.chiefAccountant || "",
          legalRepresentative: apiData.legalRepresentative || "",
        };

        setFormInitials(loadedData);
        setGetData(apiData);
      } catch (error) {
        console.error("Lỗi load Sổ S41a-DN:", error);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const handleBack = () => navigation(-1);

  const submitForm = async (fValues) => {
    try {
      const num = (val) => (val && val !== "" && !isNaN(val) ? Number(val) : 0);
      const str = (val) => (val != null ? val.toString() : "");

      const payload = {
        unit: fValues.unit || "",
        address: fValues.address || "",
        fiscalYear: fValues.fiscalYear || "",
        groups: (fValues.groups || []).map((g) => ({
          jointVentureName: g.jointVentureName || "",
          items: (g.items || []).map((item) => ({
            documentNumber: item.documentNumber || "",
            documentDate: item.documentDate || "",
            description: item.description || "",
            openingValue: num(item.openingValue),
            profitShareAdjustment: num(item.profitShareAdjustment),
            differentReportingDateAdjustment: num(item.differentReportingDateAdjustment),
            policyAdjustment: num(item.policyAdjustment),
            equityAdjustment: num(item.equityAdjustment),
            closingValue: num(item.closingValue),
          })),
        })),
        totalPages: str(fValues.totalPages) || "1",
        openingDate: fValues.openingDate || "",
        reportDate: `${fValues.signingDay.padStart(2, "0")}/${fValues.signingMonth.padStart(
          2,
          "0"
        )}/${fValues.signingYear}`,
        ledgerClerk: fValues.ledgerClerk || "",
        chiefAccountant: fValues.chiefAccountant || "",
        legalRepresentative: fValues.legalRepresentative || "",
      };

      console.log("🚀 SUBMIT PAYLOAD S41a-DN:", payload);

      if (!id) {
        await JointVentureInvestmentDetailedRecordService.create(payload);
        toast.success("Tạo sổ thành công!");
        navigation("/templates/joint-venture-investment-detailed-record");
      } else {
        await JointVentureInvestmentDetailedRecordService.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
    } catch (error) {
      console.error("Lỗi lưu:", error);
      toast.error(error?.response?.data?.message || "Lưu thất bại!");
    }
  };

  const handlePrint = (pValues) => {
    const html = getPrintTemplate(pValues);
    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>In Sổ S41a-DN</title>
          <style>@media print { @page { size: A4 landscape; margin: 1cm; } }</style>
        </head>
        <body onload="setTimeout(() => [window.print(), window.close()], 500)">${html}</body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Formik initialValues={formInitials} enableReinitialize onSubmit={submitForm}>
              {({ values, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <SoftBox p={2}>
                      <JointVentureInvestmentDetailedRecordInfo
                        formData={{ values, setFieldValue, containView }}
                      />
                      <SoftBox mt={10} display="flex" justifyContent="space-between">
                        <SoftButton variant="gradient" color="light" onClick={handleBack}>
                          Trở về
                        </SoftButton>
                        <SoftBox display="flex" gap={2}>
                          {containView ? (
                            <SoftButton variant="gradient" color="success" onClick={() => handlePrint(values)}>
                              In Sổ S41a-DN
                            </SoftButton>
                          ) : (
                            <SoftButton disabled={isSubmitting} type="submit" variant="gradient" color="dark">
                              {id ? "Lưu" : "Tạo mới"}
                            </SoftButton>
                          )}
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default JointVentureInvestmentDetailedRecordDetail;
